import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, Mail, ArrowLeft } from "lucide-react";

function OtpInput({ length = 6, value, onChange }) {
  const inputs = useRef([]);

  const handleChange = (idx, val) => {
    if (!/^\d?$/.test(val)) return;
    const arr = value.split("");
    arr[idx] = val;
    const next = arr.join("").slice(0, length);
    onChange(next);
    if (val && idx < length - 1) inputs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !value[idx] && idx > 0) {
      inputs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (paste) { onChange(paste); inputs.current[Math.min(paste.length, length - 1)]?.focus(); }
    e.preventDefault();
  };

  return (
    <div className="flex gap-2 justify-center" data-testid="otp-input-group">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={el => inputs.current[i] = el}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ""}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          onPaste={handlePaste}
          data-testid={`otp-digit-${i}`}
          className="w-11 h-13 text-center text-xl font-bold border border-border bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-[#FFD60A] focus:border-[#FFD60A] transition-all"
        />
      ))}
    </div>
  );
}

export default function Login() {
  const { login, register, requestOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  // OTP state
  const [otpMode, setOtpMode] = useState(false);
  const [otpStep, setOtpStep] = useState("email"); // "email" | "code"
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => { document.title = "Sign In | The Rig - Coworking & Meeting Spaces"; }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", password: "" });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(loginForm.email, loginForm.password);
      toast.success("Welcome back!");
      const role = res.user.role;
      navigate(role === "admin" ? "/admin" : role === "manager" ? "/manager" : role === "member" ? "/dashboard" : "/spaces");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(regForm.name, regForm.email, regForm.password, "guest");
      toast.success("Account created!");
      navigate("/spaces");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await requestOtp(otpEmail);
      toast.success("Passcode sent to your email");
      setOtpStep("code");
      setCooldown(60);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send passcode");
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otpCode.length < 6) { toast.error("Please enter the full 6-digit code"); return; }
    setLoading(true);
    try {
      const res = await verifyOtp(otpEmail, otpCode);
      toast.success("Welcome back!");
      const role = res.user.role;
      navigate(role === "admin" ? "/admin" : role === "manager" ? "/manager" : role === "member" ? "/dashboard" : "/spaces");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid passcode");
      setOtpCode("");
    } finally { setLoading(false); }
  };

  const resetOtp = () => {
    setOtpMode(false);
    setOtpStep("email");
    setOtpCode("");
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4 py-16">
      <title>Sign In | The Rig - Coworking & Meeting Spaces</title>
      <meta name="description" content="Sign in to your Rig account to book meeting rooms, manage your team, and access member benefits. New users can register for free." />
      <meta property="og:title" content="Sign In | The Rig" />
      <meta property="og:description" content="Sign in to book meeting rooms and access your Rig dashboard." />
      <meta property="og:type" content="website" />
      <Card className="w-full max-w-md border border-border rounded-none animate-fade-in" data-testid="auth-card">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <img src="https://customer-assets.emergentagent.com/job_2011e831-9e14-4050-89e9-67f554973600/artifacts/1oxcap9f_Logo%20The%20Rig.svg" alt="The Rig" className="h-14" />
          </div>
          <CardTitle className="font-heading text-2xl">Welcome</CardTitle>
          <CardDescription>
            {otpMode ? "Sign in with a one-time passcode" : "Sign in to book spaces or create an account"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {otpMode ? (
            <div className="space-y-4" data-testid="otp-login-section">
              {otpStep === "email" ? (
                <form onSubmit={handleRequestOtp} className="space-y-4">
                  <div>
                    <Label htmlFor="otp-email">Email Address</Label>
                    <Input
                      id="otp-email"
                      type="email"
                      placeholder="you@example.com"
                      value={otpEmail}
                      onChange={e => setOtpEmail(e.target.value)}
                      required
                      data-testid="otp-email-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    We'll send a 6-digit passcode to your email. It expires in 5 minutes.
                  </p>
                  <Button
                    type="submit"
                    className="w-full bg-foreground text-background hover:bg-foreground/90 rounded-none"
                    disabled={loading}
                    data-testid="otp-send-btn"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    {loading ? "Sending..." : "Send Passcode"}
                  </Button>
                  <button
                    type="button"
                    onClick={resetOtp}
                    className="w-full flex items-center justify-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="otp-back-to-password"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Back to password sign in
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOtp} className="space-y-5">
                  <div className="text-center space-y-1">
                    <p className="text-sm text-muted-foreground">
                      Enter the code sent to
                    </p>
                    <p className="text-sm font-medium" data-testid="otp-sent-email">{otpEmail}</p>
                  </div>
                  <OtpInput value={otpCode} onChange={setOtpCode} />
                  <Button
                    type="submit"
                    className="w-full bg-foreground text-background hover:bg-foreground/90 rounded-none"
                    disabled={loading || otpCode.length < 6}
                    data-testid="otp-verify-btn"
                  >
                    <KeyRound className="h-4 w-4 mr-2" />
                    {loading ? "Verifying..." : "Sign In"}
                  </Button>
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => { setOtpStep("email"); setOtpCode(""); }}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                      data-testid="otp-change-email"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" /> Change email
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        if (cooldown > 0) return;
                        try {
                          await requestOtp(otpEmail);
                          toast.success("New passcode sent");
                          setCooldown(60);
                          setOtpCode("");
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Failed to resend");
                        }
                      }}
                      disabled={cooldown > 0}
                      className={`text-sm transition-colors ${cooldown > 0 ? "text-muted-foreground/50 cursor-not-allowed" : "text-[#0A6E6E] hover:text-[#0A6E6E]/80 font-medium"}`}
                      data-testid="otp-resend-btn"
                    >
                      {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          ) : (
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@example.com"
                      value={loginForm.email}
                      onChange={e => setLoginForm({ ...loginForm, email: e.target.value })}
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showPw ? "text" : "password"}
                        placeholder="Enter your password"
                        value={loginForm.password}
                        onChange={e => setLoginForm({ ...loginForm, password: e.target.value })}
                        required
                        data-testid="login-password-input"
                      />
                      <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowPw(!showPw)}>
                        {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-foreground text-background hover:bg-foreground/90 rounded-none" disabled={loading} data-testid="login-submit-btn">
                    {loading ? "Signing in..." : "Sign In"}
                  </Button>
                </form>
                <div className="relative my-5">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border"></div></div>
                  <div className="relative flex justify-center"><span className="bg-card px-3 text-xs text-muted-foreground uppercase tracking-wider">or</span></div>
                </div>
                <button
                  type="button"
                  onClick={() => setOtpMode(true)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 border border-border text-sm font-medium hover:bg-muted/50 transition-colors"
                  data-testid="switch-to-otp-btn"
                >
                  <KeyRound className="h-4 w-4" />
                  Sign in with passcode
                </button>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="reg-name">Full Name</Label>
                    <Input
                      id="reg-name"
                      placeholder="John Doe"
                      value={regForm.name}
                      onChange={e => setRegForm({ ...regForm, name: e.target.value })}
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reg-email">Email</Label>
                    <Input
                      id="reg-email"
                      type="email"
                      placeholder="you@example.com"
                      value={regForm.email}
                      onChange={e => setRegForm({ ...regForm, email: e.target.value })}
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reg-password">Password</Label>
                    <Input
                      id="reg-password"
                      type="password"
                      placeholder="Create a password"
                      value={regForm.password}
                      onChange={e => setRegForm({ ...regForm, password: e.target.value })}
                      required
                      minLength={6}
                      data-testid="register-password-input"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-foreground text-background hover:bg-foreground/90 rounded-none" disabled={loading} data-testid="register-submit-btn">
                    {loading ? "Creating account..." : "Create Account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
